/**
 * parse con tolerancia al campo `data` de la notificación
 */
import { formatDateTime } from "./utils";

// ——— Greeting SIEMPRE desde users/me-full ———
async function getUserGreeting(api, notif) {
  try {
    const me = await fetchDetails(api, "users/me-full", notif);
    const directName = me?.data?.name;
    if (typeof directName === "string" && directName.trim()) {
      return `Hello, ${directName.trim()}!`;
    }
    // Fallback
    const u = me?.user || me?.data || me;
    const fn = u?.first_name || u?.name || u?.firstName || u?.username;
    const ln = u?.last_name || u?.lastName || u?.surname || "";
    const full = [fn, ln].filter(Boolean).join(" ").trim();
    return full ? `Hello, ${full}!` : "Hello!";
  } catch {
    return "Hello!";
  }
}

function extractIdFromUrl(u) {
  if (!u) return null;
  const s = String(u);
  const m1 = s.match(/\/categories\/(\d+)(?:\/|$)/);
  if (m1) return Number(m1[1]);
  const m2 = s.match(/\/subcategory\/(\d+)(?:\/|$)/);
  if (m2) return Number(m2[1]);
  const m3 = s.match(/\/(\d+)(?:\/|$)/);
  if (m3) return Number(m3[1]);
  return null;
}

function parseSafeJson(val) {
  try {
    if (!val) return {};
    return typeof val === "string" ? JSON.parse(val) : val || {};
  } catch {
    return {};
  }
}

/**
 * instancia axios `api`
 */
async function fetchDetails(api, path, notif) {
  let normalized = String(path || "").trim();

  // Si explícitamente piden users/me-full, NO sobreescribir por origin
  const wantsMeFull = normalized.replace(/^\/+/, "") === "users/me-full";

  if (!wantsMeFull) {
    if (notif?.origin === "cms_support_ticket_created") {
      const dataObj = parseSafeJson(notif.data);
      const ticketId = dataObj?.ticket_id;
      if (ticketId) {
        normalized = `support-assist/request/${ticketId}`;
      }
    } else if (notif?.origin === "cms_group_approval_pending") {
      const dataObj = parseSafeJson(notif.data);
      const group_id = dataObj?.group_id;
      if (group_id) {
        // ojo con los slashes dobles
        normalized = `chats/group/${group_id}/info`;
      }
    }
  }

  // Normaliza slashes (quita iniciales y dobles)
  normalized = normalized.replace(/^\/+/, "").replace(/\/\/+/g, "/");

  const res = await api.get(normalized);
  return res.data;
}

/**
 * Info para el caso de support ticket redirection
 */
function mapSupportToUI(s) {
  if (!s) return null;
  return {
    ...s,
    id: s.id ?? s.ticket_id ?? s.folio_number,
    folio: s.folio_number || s.id,
    email: s.contact_email || "-",
    contact_number: s.contact_number || "-",
    reason: s.reason || "-",
    description: s.description || "-",
    requested_at: s.requested_at || null,
    status: s.status || "",
    agent: s.agent || null,
    user: s.user || null,
    group: s.group || null,
  };
}

/**
 * Resuelve y muestra el modal de acción para una notificación CMS.
 *
 * @param {Object} notif - Notificación completa
 * @param {Object} ctx - Dependencias y setters
 * @param {import('axios').AxiosInstance} ctx.api - instancia axios
 * @param {Function} ctx.navigate - navigate de react-router
 * @param {Function} ctx.setActionOpen
 * @param {Function} ctx.setActionLoading
 * @param {Function} ctx.setActionTitle
 * @param {Function} ctx.setActionGreeting
 * @param {Function} ctx.setActionMessage
 * @param {Function} ctx.setActionDetails
 * @param {Function} ctx.setActionButtons
 */
export async function resolveCmsAction(notif, ctx) {
  if (!notif) return;

  const {
    api,
    navigate,
    setActionOpen,
    setActionLoading,
    setActionTitle,
    setActionGreeting,
    setActionMessage,
    setActionDetails,
    setActionButtons,
  } = ctx;

  setActionTitle(notif.title || "");
  setActionMessage(notif.message || "");

  const origin = notif.origin;
  const detailsPath = notif.url;
  const dataObj = parseSafeJson(notif.data);

  // Botones por defecto
  let buttons = {
    showReject: false,
    showApprove: false,
    showMore: false,
    rejectText: "Cancel",
    approveText: "Accept",
    moreText: "More details",
    onReject: null,
    onApprove: null,
    onMore: null,
  };

  // Caso 1: cambio de rol de usuario
  if (origin === "cms_user_role_updated") {
    buttons = {
      ...buttons,
      showMore: true,
      moreText: "Show profile",
      onMore: () => {
        setActionOpen(false);
        navigate("/profile");
      },
    };
  }

  // Caso 2: permisos de módulo de rol
  if (origin === "cms_role_module_permission_updated") {
    const roleId = dataObj?.role_id;
    buttons = {
      ...buttons,
      showMore: true,
      moreText: "Open role details",
      onMore: () => {
        setActionOpen(false);
        if (roleId) {
          navigate(`/dashboard/role/edit-role/${roleId}`);
        } else {
          navigate("/dashboard/role");
        }
      },
    };
    const greeting = await getUserGreeting(api, notif);
    setActionGreeting(greeting);
  }

  // Caso 3: soporte - nuevo ticket
  // Mapeado directamente en el try por necesidad de enviar data

  // Caso 4: aprobación de grupo (approve / reject)
  if (origin === "cms_group_approval_pending") {
    const groupId = dataObj?.group_id;
    const approveUrl = dataObj?.urls?.approve; // ej: "/chats/group/290/status"

    // helpers locales para PUT x-www-form-urlencoded
    const putApprove = async (activate) => {
      if (!approveUrl) return;
      const normalized = String(approveUrl).replace(/^\/+/, ""); // "chats/group/290/status"
      const body = new URLSearchParams();
      body.set("activate", String(!!activate));
      setActionLoading(true);
      try {
        await api.put(normalized, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        // éxito: cerramos modal
        setActionOpen(false);
      } catch (e) {
        console.error("Group approval update failed:", e);
        // opcional: podrías setear un mensaje de error aquí si quieres
      } finally {
        setActionLoading(false);
      }
    };

    buttons = {
      ...buttons,
      showReject: true,
      rejectText: "Reject",
      onReject: () => {
        void putApprove(false);
      },
      showApprove: true,
      approveText: "Approve",
      onApprove: () => {
        void putApprove(true);
      },
      showMore: true,
      moreText: "More details",
      onMore: () => {
        setActionOpen(false);
        if (groupId) {
          navigate(`/groups/group-details/${groupId}`);
        } else {
          navigate("/groups");
        }
      },
    };
    const greeting = await getUserGreeting(api, notif);
    setActionGreeting(greeting);
  }

  // Caso 5: aprobación de subcategoría
  if (origin === "cms_service_subcategory_approval") {
    const approveUrl = dataObj?.urls?.approve;

    const doApprove = async () => {
      if (!approveUrl) return;
      const normalized = String(approveUrl).replace(/^\/+/, "");
      setActionLoading(true);
      try {
        // PUT sin body (endpoint de una sola vez)
        await api.put(normalized);
        setActionOpen(false);
      } catch (e) {
        console.error("Subcategory approve failed:", e);
      } finally {
        setActionLoading(false);
      }
    };

    const greeting = await getUserGreeting(api, notif);
    setActionGreeting(greeting);

    buttons = {
      ...buttons,
      showReject: true,
      rejectText: "Reject",
      onReject: () => {
        setActionOpen(false);
      },

      showApprove: true,
      approveText: "Approve",
      onApprove: () => {
        void doApprove();
      },

      // More details → support/details/{id}
      showMore: true,
      moreText: "More details",
      onMore: async () => {
        setActionLoading(true);
        try {
          const subId =
            extractIdFromUrl(dataObj?.urls?.approve) ??
            extractIdFromUrl(detailsPath) ??
            dataObj?.subcategory_id ??
            dataObj?.request_id ??
            notif.id;

          // Traer el soporte real
          const supportData = await fetchDetails(
            api,
            `support-assist/request/${subId}`,
            notif,
          );
          const supportLike =
            supportData?.ticket || supportData?.data || supportData;
          const mapped = mapSupportToUI(supportLike);

          setActionOpen(false);
          navigate(`/support/details/${subId}`, {
            state: { support: mapped, action: "view", from: "notification" },
          });
        } catch (e) {
          console.error("Prefetch support for subcategory failed:", e);
          // Fallback
          setActionOpen(false);
          const subIdFallback =
            extractIdFromUrl(dataObj?.urls?.approve) ??
            extractIdFromUrl(detailsPath) ??
            dataObj?.subcategory_id ??
            dataObj?.request_id ??
            notif.id;

          const pseudoSupport = {
            id: subIdFallback,
            folio_number: subIdFallback,
            status: "pending",
            reason: `Subcategory approval: ${dataObj?.subcategory_name || "-"}`,
            description: `Request to create subcategory '${dataObj?.subcategory_name || "-"}' in '${dataObj?.category_name || "-"}'.`,
            requested_at: notif.created_at || notif.timestamp,
            user: { id: dataObj?.requester_id },
          };

          const mapped = mapSupportToUI(pseudoSupport);

          navigate(`/support/details/${subIdFallback}`, {
            state: { support: mapped, action: "view", from: "notification" },
          });
        } finally {
          setActionLoading(false);
        }
      },
    };
  }

  // Caso 6: ticket de soporte asignado
  if (origin === "cms_support_ticket_assigned") {
    const ticketId =
      dataObj?.ticket_id ?? extractIdFromUrl(detailsPath) ?? notif.id;

    const greeting = await getUserGreeting(api, notif);
    setActionGreeting(greeting);

    const goCheck = async () => {
      setActionLoading(true);
      try {
        const supportData = await fetchDetails(
          api,
          `support-assist/request/${ticketId}`,
          notif,
        );
        const supportLike =
          supportData?.ticket || supportData?.data || supportData;
        const mapped = mapSupportToUI(supportLike);

        // El CheckSupport usa support.user_id
        if (!mapped.user_id) {
          mapped.user_id = supportLike?.user?.id ?? dataObj?.user_id ?? null;
        }

        setActionOpen(false);
        navigate(`/support/check/${ticketId}`, {
          state: { support: mapped, action: "check", from: "notification" },
        });
      } catch (e) {
        console.error("Prefetch support (assigned) failed:", e);
        // Fallback
        const pseudo = {
          id: ticketId,
          folio_number: ticketId,
          status: "pending",
          reason: dataObj?.ticket_title || "Support ticket",
          description: notif.content || "",
          requested_at: notif.created_at || notif.timestamp,
          user: { id: dataObj?.user_id },
        };
        const mapped = mapSupportToUI(pseudo);
        if (!mapped.user_id) mapped.user_id = dataObj?.user_id ?? null;

        setActionOpen(false);
        navigate(`/support/check/${ticketId}`, {
          state: { support: mapped, action: "check", from: "notification" },
        });
      } finally {
        setActionLoading(false);
      }
    };

    buttons = {
      ...buttons,
      showMore: true,
      moreText: "More details",
      onMore: () => {
        void goCheck();
      },
    };
  }

  // Abrir modal + cargar detalles (si hay URL)
  setActionLoading(true);
  setActionDetails([]);
  if (origin === "cms_user_role_updated") setActionGreeting("");
  setActionButtons(buttons);
  setActionOpen(true);

  try {
    if (detailsPath) {
      const data = await fetchDetails(api, detailsPath, notif);

      if (origin === "cms_user_role_updated") {
        // Primer caso
        const userLike = data?.user || data?.data || data;
        const first =
          userLike?.first_name ||
          userLike?.name ||
          userLike?.firstName ||
          userLike?.username;
        const last =
          userLike?.last_name || userLike?.lastName || userLike?.surname || "";
        const fullName = [first, last].filter(Boolean).join(" ").trim();
        setActionGreeting(fullName ? `Hello, ${fullName}!` : "Hello!");

        const role =
          userLike?.role ||
          userLike?.roles?.[0] ||
          userLike?.current_role ||
          notif.message?.match(/'([^']+)'/)?.[1] ||
          "N/A";

        const email =
          userLike?.email || userLike?.mail || userLike?.contactEmail || null;

        const rows = [
          { label: "User", value: fullName || email || "—" },
          { label: "Role", value: String(role || "—") },
          {
            label: "Updated at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ].filter((r) => r.value && r.value !== "—");

        setActionDetails(rows);
      } else if (origin === "cms_role_module_permission_updated") {
        // Segundo caso
        const metaRows = [
          dataObj?.role_name
            ? { label: "Role", value: dataObj.role_name }
            : null,
          {
            label: "Updated at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ].filter(Boolean);

        // info del endpoint url (si la hay)
        const roleLike = data?.role || data?.data || data;

        const serverRows = [];

        if (roleLike?.name) {
          serverRows.push({ label: "Role name", value: String(roleLike.name) });
        }

        if (Array.isArray(roleLike?.modules) && roleLike.modules.length > 0) {
          const allModules = roleLike.modules.map((m) => m.name).join(", ");
          serverRows.push({ label: "Module(s)", value: allModules });

          roleLike.modules.forEach((mod) => {
            if (Array.isArray(mod.permissions) && mod.permissions.length > 0) {
              const perms = mod.permissions.map((p) => p.name).join(", ");
              serverRows.push({
                label: `Permissions (${mod.name})`,
                value: perms,
              });
            }
          });
        }

        setActionDetails([...metaRows, ...serverRows]);
      } else if (origin === "cms_support_ticket_created") {
        // Tercer caso
        const supportLike = data?.ticket || data?.data || data;

        const rows = [
          supportLike?.folio_number != null && {
            label: "Folio",
            value: String(supportLike.folio_number),
          },
          dataObj?.ticket_id != null && {
            label: "Ticket ID",
            value: String(dataObj.ticket_id),
          },
          dataObj?.ticket_title && {
            label: "Reason",
            value: String(dataObj?.ticket_title),
          },
          dataObj?.priority && {
            label: "Priority",
            value: String(dataObj.priority),
          },
          supportLike?.status && {
            label: "Status",
            value: String(supportLike.status.replace(/_/g, " ").toLowerCase()),
          },
          supportLike?.contact_email && {
            label: "Contact email",
            value: String(supportLike.contact_email),
          },
          {
            label: "Created at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ].filter(Boolean);

        if (supportLike?.description) {
          setActionMessage(supportLike.description);
        }
        setActionDetails(rows);

        const mapped = mapSupportToUI(supportLike);
        setActionButtons((prev) => ({
          ...prev,
          showMore: true,
          moreText: "More details",
          onMore: () => {
            setActionOpen(false);
            const id = mapped?.id ?? dataObj?.ticket_id;
            if (id) {
              navigate(`/support/details/${id}`, {
                state: {
                  support: mapped,
                  action: "view",
                  from: "notification",
                },
              });
            } else {
              navigate("/support");
            }
          },
        }));
      } else if (origin === "cms_group_approval_pending") {
        // Cuarto caso
        const groupLike = data?.group || data?.data || data;
        try {
          const greeting = await getUserGreeting(api, notif);
          setActionGreeting(greeting);
        } catch {
          /* silencioso */
        }

        const owner = Array.isArray(groupLike?.participants)
          ? groupLike.participants.find((p) => p?.role === "owner") ||
            groupLike.participants[0]
          : null;

        const administratorName = owner?.name || "-";
        const organizationLabel =
          groupLike?.organization_name ||
          (groupLike?.organization_type != null
            ? String(groupLike.organization_type)
            : "-");

        const originLabel = groupLike?.is_group ? "Group" : "Direct";

        const rows = [
          groupLike?.name && { label: "Group", value: String(groupLike.name) },
          { label: "Administrator", value: administratorName },
          { label: "Organization", value: organizationLabel },
          { label: "Origin", value: originLabel },
        ].filter(Boolean);
        setActionDetails(rows);
      } else if (origin === "cms_service_subcategory_approval") {
        // Quinto caso
        //  datos principales del request
        const rows = [
          dataObj?.subcategory_name && {
            label: "Subcategory",
            value: String(dataObj.subcategory_name),
          },
          dataObj?.category_name && {
            label: "Category",
            value: String(dataObj.category_name),
          },
          dataObj?.requester_id != null && {
            label: "Requester ID",
            value: String(dataObj.requester_id),
          },
          // dataObj?.priority         && { label: 'Priority', value: String(dataObj.priority) },
          {
            label: "Created at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ].filter(Boolean);

        setActionMessage(
          `Request to create subcategory '${dataObj?.subcategory_name || "-"}' in '${dataObj?.category_name || "-"}'.`,
        );

        setActionDetails(rows);
      } else if (origin === "cms_support_ticket_assigned") {
        // Sexto caso
        const supportLike = data?.ticket || data?.data || data;

        const rows = [
          supportLike?.folio_number != null && {
            label: "Folio",
            value: String(supportLike.folio_number),
          },
          dataObj?.ticket_id != null && {
            label: "Ticket ID",
            value: String(dataObj.ticket_id),
          },
          dataObj?.ticket_title && {
            label: "Title",
            value: String(dataObj.ticket_title),
          },
          supportLike?.status && {
            label: "Status",
            value: String(supportLike.status),
          },
          supportLike?.user?.email && {
            label: "Contact email",
            value: String(supportLike.user.email),
          },
          {
            label: "Created at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ].filter(Boolean);

        setActionDetails(rows);
      } else {
        setActionDetails([
          {
            label: "Updated at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ]);
      }
    } else {
      // fallback sin detailsPath
      if (origin === "cms_role_module_permission_updated") {
        const rows = [
          dataObj?.role_name
            ? { label: "Role", value: dataObj.role_name }
            : null,
          dataObj?.role_id
            ? { label: "Role ID", value: String(dataObj.role_id) }
            : null,
          dataObj?.priority
            ? { label: "Priority", value: String(dataObj.priority) }
            : null,
          dataObj?.action_required
            ? { label: "Action", value: String(dataObj.action_required) }
            : null,
          {
            label: "Updated at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ].filter(Boolean);
        setActionDetails(rows);
      } else {
        const greeting = await getUserGreeting(api, notif);
        setActionGreeting(greeting);
        setActionDetails([
          {
            label: "Updated at",
            value: formatDateTime(new Date(notif.timestamp)),
          },
        ]);
      }
    }
  } catch (err) {
    console.error("Error loading action details:", err);
    // fallback
    if (origin === "cms_role_module_permission_updated") {
      const rows = [
        dataObj?.role_name ? { label: "Role", value: dataObj.role_name } : null,
        dataObj?.role_id
          ? { label: "Role ID", value: String(dataObj.role_id) }
          : null,
        dataObj?.priority
          ? { label: "Priority", value: String(dataObj.priority) }
          : null,
        dataObj?.action_required
          ? { label: "Action", value: String(dataObj.action_required) }
          : null,
        {
          label: "Updated at",
          value: formatDateTime(new Date(notif.timestamp)),
        },
      ].filter(Boolean);
      setActionDetails(rows);
    } else if (origin === "cms_support_ticket_created") {
      const rows = [
        dataObj?.ticket_id
          ? { label: "Ticket ID", value: String(dataObj.ticket_id) }
          : null,
        dataObj?.ticket_title
          ? { label: "Title", value: dataObj.ticket_title }
          : null,
        dataObj?.priority
          ? { label: "Priority", value: dataObj.priority }
          : null,
        dataObj?.action_required
          ? { label: "Action", value: dataObj.action_required }
          : null,
        {
          label: "Created at",
          value: formatDateTime(new Date(notif.timestamp)),
        },
      ].filter(Boolean);
      setActionDetails(rows);
    } else if (origin === "cms_group_approval_pending") {
      const rows = [
        dataObj?.group_id && {
          label: "Group",
          value: String(dataObj.group_id),
        },
        dataObj?.creator_id && {
          label: "Administrator",
          value: String(dataObj.creator_id),
        },
        dataObj?.priority && {
          label: "Organization",
          value: String(dataObj.priority),
        },
        { label: "Origin", value: "Group" },
      ].filter(Boolean);
      setActionDetails(rows);
    } else if (origin === "cms_service_subcategory_approval") {
      const rows = [
        dataObj?.subcategory_name && {
          label: "Subcategory",
          value: String(dataObj.subcategory_name),
        },
        dataObj?.category_name && {
          label: "Category",
          value: String(dataObj.category_name),
        },
        dataObj?.requester_id != null && {
          label: "Requester ID",
          value: String(dataObj.requester_id),
        },
        dataObj?.priority && {
          label: "Priority",
          value: String(dataObj.priority),
        },
        {
          label: "Created at",
          value: formatDateTime(new Date(notif.timestamp)),
        },
      ].filter(Boolean);
      setActionDetails(rows);
    } else if (origin === "cms_support_ticket_assigned") {
      const rows = [
        dataObj?.ticket_id != null && {
          label: "Ticket ID",
          value: String(dataObj.ticket_id),
        },
        dataObj?.ticket_title && {
          label: "Title",
          value: String(dataObj.ticket_title),
        },
        dataObj?.user_id != null && {
          label: "User ID",
          value: String(dataObj.user_id),
        },
        dataObj?.agent_id != null && {
          label: "Agent ID",
          value: String(dataObj.agent_id),
        },
        {
          label: "Created at",
          value: formatDateTime(new Date(notif.timestamp)),
        },
      ].filter(Boolean);
      setActionDetails(rows);
    } else {
      const greeting = await getUserGreeting(api, notif);
      setActionGreeting(greeting);
      setActionDetails([
        {
          label: "Updated at",
          value: formatDateTime(new Date(notif.timestamp)),
        },
      ]);
    }
  } finally {
    setActionLoading(false);
  }
}
